import { accessEnum } from "components/access-control/accessEnum";
import NexaMark from "pages/nexa-ai/components/NexaMark";

export interface TabItem {
  name: string;
  link: string;
  icon: React.ReactNode;
  showInternal?: boolean;
  id: string;
  subItem?: TabItem[];
  accessKey?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

export interface RootState {
  sidebar?: {
    isOpen?: boolean;
    expandedTabs?: Record<string, boolean>;
  };
  profiledata: {
    data: {
      email?: string;
    };
  };
  userAccess?: {
    accessKeys?: Record<string, boolean>;
    disabledKeys?: Record<string, boolean>;
    onboardingStatus?: Record<string, boolean>;
  };
}

export const landedCostTabsEnum = {
  upperTabs: [
    {
      id: "landed-cost-main",
      name: "",
      icon: "",
      subItem: [
        {
          name: "Landed Cost Calculator",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">calculate</span>,
          link: "/landedcostcalculator",
        },
        {
          name: "Compare Tariff",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">bar_chart</span>,
          link: "/compare-tariff",
        },
      ],
    },
  ],
  bottomTabs: [
    {
      id: "landed-cost-dashboard",
      name: "",
      icon: "",
      subItem: [
        {
          name: "Dashboard",
          link: "/tc-dashboard?DT=landedcostcalculator",
          icon: <span className="material-symbols-outlined tw-text-[16px]">update</span>,
        },
      ],
    },
  ],
};

export const gtcTabsEnum = {
  upperTabs: [
    {
      id: "nexa-ai",
      name: "",
      icon: "",
      collapsible: false,
      subItem: [
        {
          name: "Nexa AI",
          link: "/nexa-ai",
          icon: <NexaMark size={16} />,
          accessKey: accessEnum.NEXA_AI_MODULE_ACCESS,
        },
      ],
    },
    {
      id: "gtc-workflows",
      name: "Workflows",
      icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-500">clear_all</span>,
      subItem: [
        {
          name: "All Requests",
          link: "/all-requests",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">grid_view</span>,
          accessKey: accessEnum.CLASSIFICATION_REQUEST_MODULE,
        },
        {
          name: "My Requests",
          link: "/my-tasks",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">view_agenda</span>,
          accessKey: accessEnum.MY_TASKS_MODULE_ACCESS,
        },
        {
          name: "Supplier Solicitation",
          link: "/supplier-solicitation",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">group_search</span>,
          accessKey: accessEnum.SUPPLIER_SOLICITATION_MODULE_ACCESS,
        },
      ],
    },
    {
      id: "gtc-master-data",
      name: "Master Data Management",
      icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-500">database</span>,
      subItem: [
        {
          name: "All SKUs",
          link: "/all-skus",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">package_2</span>,
          accessKey: accessEnum.ALL_SKUS_MODULE_ACCESS,
        },
        {
          name: "BOM Master",
          link: "/bom-master",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">receipt</span>,
          accessKey: accessEnum.BOM_MASTER_MODULE_ACCESS,
        },
        {
          name: "Partner Master",
          link: "/partner-master",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">apartment</span>,
          accessKey: accessEnum.PARTNER_MASTER_MODULE_ACCESS,
        },
        {
          name: "Trade Lanes Master",
          link: "/trade-lanes",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">format_list_bulleted</span>,
          accessKey: accessEnum.TRADE_LANE_MASTER_MODULE_ACCESS,
        },
      ],
    },
    {
      id: "gtc-reports-alerts",
      name: "Reports & Alerts",
      defaultCollapsed: true,
      icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-500">insights</span>,
      subItem: [
        {
          name: "Dashboard",
          link: "/dashboard",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">monitor</span>,
          accessKey: accessEnum.REQUESTS_DASHBOARD_MODULE_ACCESS,
        },
        {
          name: "Watchlist",
          link: "/watchlist",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">hotel_class</span>,
          accessKey: accessEnum.WATCHLIST_MODULE_ACCESS,
        }
      ],
    },
    {
      id: "gtc-content",
      name: "Content",
      icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-500">assured_workload</span>,
      subItem: [
        {
          name: "Tariff, Duties and FTAs",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">category</span>,
          link: "/classify",
          accessKey: accessEnum.TARIFF_DUTIES_MODULE_ACCESS,
        },
        {
          name: "Controls Search",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">gavel</span>,
          link: "/controls",
          accessKey: accessEnum.CONTROLS_SEARCH_MODULE_ACCESS,
        },
        {
          name: "ECCN Search",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">frame_inspect</span>,
          link: "/eccn-classification",
          accessKey: accessEnum.ECCN_SEARCH_MODULE_ACCESS,
        },
        {
          name: "License Determination",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">social_leaderboard</span>,
          link: "/eccn-license-determination",
          accessKey: accessEnum.ECCN_LICENSES_DETERMINATION_MODULE_ACCESS,
        },
        {
          name: "Document Determination",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">assignment_ind</span>,
          link: "/document-determination",
          accessKey: accessEnum.DOCUMENT_DETERMINATION_MODULE_ACCESS,
        },
        {
          name: "Embargoes",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">space_dashboard</span>,
          link: "/embargoes",
          accessKey: accessEnum.EMBARGOES_MODULE_ACCESS,
        },
        {
          name: "HS Classifier",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">account_tree</span>,
          link: "/hs-classifier",
        },
        {
          name: "ECCN Classifier",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">policy</span>,
          link: "/eccn-classifier",
        },
      ],
    },
    {
      id: "gtc-upload-logs",
      name: "",
      icon: "",
      collapsible: false,
      subItem: [
        {
          name: "Upload Logs",
          link: "/upload-logs",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">dialogs</span>,
          accessKey: accessEnum.UPLOAD_LOGS_MODULE,
        },
      ],
    },
  ],
  bottomTabs: [
    {
      id: "gtc-configuration",
      name: "Configuration",
      icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-500">settings</span>,
      subItem: [
        {
          name: "Configuration",
          link: "/configuration/risk-thresholds",
          icon: <span className="material-symbols-outlined tw-text-[16px] tw-text-gray-900">build</span>,
          accessKey: accessEnum.RISK_CONFIGURATION_ACCESS,
        },
      ],
    },
  ],
};
